import React from 'react';
import styled from 'styled-components';
import Moment from 'react-moment';
import 'moment-timezone';

import CheckboxSingle from '../CheckboxSingle';
import IconsLib from '@/utils/IconsLib';
import { colors, ColorsType } from '@/constants/colors';
import { ICONS_MAP, IconType } from '@/utils/icons';
import Dropdown from '../UI/Dropdown';
// import { DropdownActionType } from '@/pages/Admin/Stores';
import { useUIStore } from '@/store/useUIStore';
import { DropdownActionType } from '@/types/shared';

interface BadgeValue {
   color: string;
   text: string;
}

// interface TitleProps {
//    textDecoration?: string; // Ensure this is the correct type
//    $textbold?: boolean; // Ensure this matches expected type
//    children: React.ReactNode;
// }

interface Props<T> {
   value: string | number | boolean | BadgeValue;
   type: string;
   checked?: boolean;
   $textbold?: boolean;
   textDecoration?: string;
   $texttransform?: string;
   extraValue?: string;
   dropdownData?: DropdownActionType<T>[];
   selected?: unknown;
   isHtml?: boolean;
}

const isBadgeValue = (val: unknown): val is BadgeValue =>
   typeof val === 'object' && val !== null && 'color' in val && 'text' in val;

const isValidDateValue = (val: unknown): val is string | number | Date => {
   return (
      typeof val === 'string' || typeof val === 'number' || val instanceof Date
   );
};
const isValidNormalTextValue = (val: unknown): val is string => {
   return typeof val === 'string';
};
const isValidIcon = (val: unknown): val is IconType => {
   return typeof val === 'string' && val in ICONS_MAP;
};

const isValidBooleanValue = (val: unknown): val is boolean => {
   return typeof val === 'boolean';
};

type InventoryValue = {
   value: string;
   title: string;
};

const isValidInventoryValue = (val: unknown): val is InventoryValue =>
   typeof val === 'object' && val !== null && 'value' in val;

type TextIndicatorValue = {
   bg: string;
   textColor: string;
   color: string;
   text: string;
};

const isValidTextIndicatorValue = (val: unknown): val is TextIndicatorValue =>
   typeof val === 'object' &&
   val !== null &&
   'bg' in val &&
   'textColor' in val &&
   'color' in val &&
   'text' in val;

const DataItem = <T,>({
   value,
   type,
   checked,
   $textbold = false,
   textDecoration,
   $texttransform,
   extraValue,
   dropdownData = [],
   selected,
   isHtml,
}: Props<T>) => {
   const { outsideClickId } = useUIStore();

   return (
      <Wrapper>
         {type === 'more-action' && (
            <BtnWrapper id={isValidNormalTextValue(value) ? value : ''}>
               <IconWrapper id={isValidNormalTextValue(value) ? value : ''}>
                  <IconsLib
                     // icon={isValidIcon(value) ? value : 'sample'}
                     icon="more-2-fill"
                     size="16px"
                     color={colors.GRAY4}
                     id={isValidNormalTextValue(value) ? value : ''}
                     // color={
                     //    isValidNormalTextValue(extraValue)
                     //       ? extraValue
                     //       : colors.GRAY4
                     // }
                  />
               </IconWrapper>
               {dropdownData.length > 0 && outsideClickId === `${value}` && (
                  <Dropdown
                     active={outsideClickId === `${value}`}
                     actions={dropdownData}
                     width="150px"
                     textdecoration="normal"
                     top="0"
                     right="22px"
                     selected={selected}
                     // selected={isValidNormalTextValue(value) ? value : ''}
                  />
               )}
            </BtnWrapper>
         )}
         {type === 'image' && (
            <ImageWrapper>
               <Img
                  src={
                     isValidNormalTextValue(value)
                        ? value
                        : '/placeholder-main.png'
                  }
                  alt={isValidNormalTextValue(value) ? value : ''}
               />
            </ImageWrapper>
         )}
         {type === 'logo' && (
            <LogoWrapper>
               <Logo
                  src={isValidNormalTextValue(value) ? value : ''}
                  alt={isValidNormalTextValue(value) ? value : ''}
               />
            </LogoWrapper>
         )}
         {type === 'checkbox' && <CheckboxSingle checked={checked ?? false} />}
         {type === 'normal' && (
            <Normal
               $texttransform={
                  isValidNormalTextValue(value) ? $texttransform ?? '' : 'none'
               }
            >
               {isValidNormalTextValue(value) ? (
                  isHtml ? (
                     <div dangerouslySetInnerHTML={{ __html: value }} />
                  ) : (
                     value
                  )
               ) : (
                  ''
               )}
            </Normal>
         )}
         {type === 'link' && (
            <Link>
               <span>{isValidNormalTextValue(value) ? value : ''}</span>{' '}
               <IconsLib
                  icon="file-copy-line"
                  size="16px"
                  color={colors.PRIMARY70}
               />
            </Link>
         )}
         {type === 'email' && (
            <Email>{isValidNormalTextValue(value) ? value : ''}</Email>
         )}

         {type === 'title' && (
            <Title
               textDecoration={
                  isValidNormalTextValue(value) ? textDecoration ?? '' : 'none'
               }
               $textbold={$textbold === true ? 'true' : 'false'}
               // $textbold={isValidBooleanValue(value) ? $textbold ?? false : false}
            >
               {isValidNormalTextValue(value) ? value : ''}
            </Title>
         )}

         {type === 'date' && isValidDateValue(value) && (
            <DateText>
               <ExtraValue> {extraValue}</ExtraValue>
               <p>
                  <Moment fromNow>
                     {isValidDateValue(value) ? new Date(value) : new Date()}
                  </Moment>
                  {/* <Moment format="D MMM, YYYY">
                     {isValidDateValue(value) ? new Date(value) : new Date()}
                  </Moment> */}
               </p>
            </DateText>
         )}
         {type === 'badge' && (
            <Badge $bg={isBadgeValue(value) ? value.color : 'transparent'}>
               {isBadgeValue(value) ? value.text : value.toString()}
            </Badge>
         )}
         {type === 'text_indicator' && (
            <TextIndicator
               $bg={isValidTextIndicatorValue(value) ? value.bg : 'transparent'}
            >
               <TextIndicatorIcon
                  $bg={
                     isValidTextIndicatorValue(value)
                        ? value.color
                        : 'transparent'
                  }
               />
               <SpanTextIndicatorLabel
                  $color={
                     isValidTextIndicatorValue(value)
                        ? value.textColor
                        : 'transparent'
                  }
               >
                  {isValidTextIndicatorValue(value) ? value.text : ''}
               </SpanTextIndicatorLabel>
            </TextIndicator>
         )}
         {type === 'inventory' && (
            <InventoryWrapper>
               <InventoryValueWrapper>
                  <InventoryValue>
                     {isValidInventoryValue(value) ? value.value : ''}
                  </InventoryValue>
               </InventoryValueWrapper>
               <InventoryTitle>
                  {isValidInventoryValue(value) ? value.title : ''}
               </InventoryTitle>
            </InventoryWrapper>
         )}
         {type === 'stars' && (
            <RatingContent>
               <IconsLib
                  icon={Number(value) > 4 ? 'star-fill' : 'star-line'}
                  size="18px"
                  color={colors.YELLOW}
                  mobilesize="12px"
               />
               <IconsLib
                  icon={Number(value) > 3 ? 'star-fill' : 'star-line'}
                  size="18px"
                  color={colors.YELLOW}
                  mobilesize="12px"
               />
               <IconsLib
                  icon={Number(value) > 2 ? 'star-fill' : 'star-line'}
                  size="18px"
                  color={colors.YELLOW}
                  mobilesize="12px"
               />
               <IconsLib
                  icon={Number(value) > 1 ? 'star-fill' : 'star-line'}
                  size="18px"
                  color={colors.YELLOW}
                  mobilesize="12px"
               />
               <IconsLib
                  icon={Number(value) > 0 ? 'star-fill' : 'star-line'}
                  size="18px"
                  color={colors.YELLOW}
                  mobilesize="12px"
               />
            </RatingContent>
         )}
      </Wrapper>
   );
};
const Wrapper = styled.div`
   font-family: Inter, sans-serif;
   user-select: none;
`;

const ImageWrapper = styled.div`
   /* background: red; */
   border: 1px solid ${colors.GRAY12};
   border-radius: 5px;
   padding: 1px;
   width: 40px;
   height: 40px;
   display: grid;
   place-items: center;
`;
const Img = styled.img`
   width: 36px;
   height: 36px;
   object-fit: cover;
   border-radius: 5px;
`;

const BtnWrapper = styled.div`
   position: relative;
   @media (max-width: 768px) {
    position: absolute;
    right: 1rem;
    top: 1rem;
   }
`;
const IconWrapper = styled.div`

   width: 25px;
   height: 25px;
   display: grid;
   place-items: center;

   border-radius: 4px;
   &:hover {
      background: ${colors.GRAY12};
   }

 

   
`;

const LogoWrapper = styled.div`
   /* background: red; */
   /* border: 1px solid ${colors.GRAY12}; */
   /* border-radius: 5px; */
   /* padding: 1px; */
   /* width: 40px;
   height: 40px; */

   width: 50px;

   display: grid;
   place-items: center;
`;
const Logo = styled.img`
   /* width: 36px;
   height: 36px;
   object-fit: cover; */
   width: 100%;
   border-radius: 5px;
`;
const InventoryWrapper = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;

const InventoryValueWrapper = styled.div`
   background: ${colors.WARNING_LIGHT};
   border-radius: 3px;
   /* width: 20px; */

   padding: 0 2px;
   min-width: 20px;
   height: 20px;
   display: grid;
   place-items: center;
`;
const InventoryValue = styled.span`
   font-size: 13px;
   /* padding: 2px 4px; */

   line-height: 1.4;
   text-transform: capitalize;
`;
const InventoryTitle = styled.p`
   font-size: 13px;
`;
const Normal = styled.div<{
   $texttransform: string;
}>`
   font-size: 13px;
   text-transform: ${({ $texttransform }) => $texttransform};
   color: ${colors.MAIN_DARK70};
`;
const Link = styled.div`
   display: flex;
   justify-content: flex-start;
   align-items: center;
   gap: 5px;
   font-size: 13px;
   text-transform: lowercase;
   color: ${colors.PRIMARY};
   /* -webkit-line-clamp: 1;  */
   &:hover {
      color: ${colors.PRIMARY};
      text-decoration: underline;
   }
`;
const DateText = styled.div`
   font-size: 12px;
   /* text-transform: capitalize; */
   color: ${colors.GRAY5};
`;
const ExtraValue = styled.p`
   font-size: 13px;
   text-transform: capitalize;
   color: ${colors.GRAY1};
`;
const Email = styled.p`
   font-size: 13px;
   color: ${colors.GRAY_DARK};
`;
const Title = styled.p<{
   textDecoration?: string;
   $textbold?: string;
}>`
   color: ${colors.MAIN_DARK90};
   font-size: 13px;
   font-weight: ${({ $textbold }) => ($textbold === 'true' ? '700' : '300')};
   /* max-width: 300px; */
   text-decoration: ${({ textDecoration }) => textDecoration};
   overflow: hidden;
   text-overflow: ellipsis;
   display: -webkit-box;
   -webkit-line-clamp: 1; /* number of lines to show */
   -webkit-box-orient: vertical;
   &:hover {
      text-decoration: underline;
   }
`;

const Badge = styled.p<{
   $bg: string;
}>`
   display: block;
   width: fit-content;
   background: ${({ $bg }) => {
      return $bg ? `${colors[$bg as ColorsType]}` : 'none';
   }};
   border-radius: 50px;
   padding: 0 10px;
   text-align: center;
   font-size: 13px;
   text-transform: capitalize;
   color: white;
`;
const TextIndicator = styled.button<{
   $bg: string;
}>`
   display: flex;
   gap: 8px;
   align-items: center;
   background: ${({ $bg }) => {
      return $bg ? `${colors[$bg as ColorsType]}` : 'none';
   }};
   border-radius: 50px;
   padding: 3px 10px;
   line-height: 1;
   text-align: center;
   font-size: 13px;
   text-transform: capitalize;
   outline: none;
   border: none;
`;
const SpanTextIndicatorLabel = styled.label<{
   $color: string;
}>`
   text-transform: lowercase;
   color: ${({ $color }) => $color};
`;
const TextIndicatorIcon = styled.span<{
   $bg: string;
}>`
   width: 8px;
   height: 8px;
   background: ${({ $bg }) => {
      return $bg ? `${colors[$bg as ColorsType]}` : 'none';
   }};
   border-radius: 50px;
`;

const RatingContent = styled.div`
   display: flex;
   gap: 5px;
   @media (max-width: 768px) {
      gap: 1px;
   }
`;

export default DataItem;
